import { ArrowDown, ArrowUpRight } from "lucide-react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";

const SERVICE_LABELS = ["Sistemas", "CRM", "Web", "Automação + IA"];

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, { stiffness: 70, damping: 24, mass: 0.5 });
  const smoothY = useSpring(pointerY, { stiffness: 70, damping: 24, mass: 0.5 });
  const imageX = useTransform(smoothX, [-1, 1], [-14, 14]);
  const imageY = useTransform(smoothY, [-1, 1], [-10, 10]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const mediaClip = useTransform(
    scrollYProgress,
    [0, 0.72],
    ["inset(12% 2.5% 7% 31% round 8px)", "inset(0% 0% 0% 0% round 0px)"],
  );
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);
  const introOpacity = useTransform(scrollYProgress, [0, 0.34, 0.55], [1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0, 0.55], [0, -72]);
  const titleLeftX = useTransform(scrollYProgress, [0, 0.58], [0, -150]);
  const titleRightX = useTransform(scrollYProgress, [0, 0.58], [0, 170]);
  const revealOpacity = useTransform(scrollYProgress, [0.42, 0.64, 0.9], [0, 1, 1]);
  const revealY = useTransform(scrollYProgress, [0.42, 0.68], [56, 0]);
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const handlePointerMove = (event) => {
    if (reduce) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    pointerX.set(((event.clientX - bounds.left) / bounds.width) * 2 - 1);
    pointerY.set(((event.clientY - bounds.top) / bounds.height) * 2 - 1);
  };

  const resetPointer = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <section
      data-header-theme="dark"
      ref={ref}
      className="hero-stage relative h-[132svh] bg-[#050606] lg:h-[176svh]"
      data-testid="hero-section"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <div className="sticky top-0 h-[100svh] min-h-[640px] overflow-hidden bg-[#050606]">
        <motion.div
          className="absolute inset-0 hidden overflow-hidden lg:block"
          style={reduce ? { clipPath: "inset(12% 2.5% 7% 31% round 8px)" } : { clipPath: mediaClip }}
          aria-hidden="true"
        >
          <motion.div
            className="hero-neutral-field absolute -inset-5"
            style={reduce ? undefined : { x: imageX, y: imageY, scale: mediaScale }}
          />
          <div className="media-scan absolute inset-0 opacity-15" />
        </motion.div>

        <motion.div className="absolute inset-x-4 bottom-[5.5rem] top-[43%] overflow-hidden rounded-md sm:inset-x-6 lg:hidden" aria-hidden="true">
          <motion.div className="hero-neutral-field absolute inset-0" style={reduce ? undefined : { scale: mediaScale }} />
          <div className="media-scan absolute inset-0 opacity-15" />
        </motion.div>

        <div className="hero-coordinate-grid absolute inset-0 opacity-55" aria-hidden="true" />

        <motion.div
          className="site-shell relative z-10 flex h-full flex-col pb-7 pt-[108px] sm:pb-8 lg:pb-10 lg:pt-[124px]"
          style={reduce ? undefined : { opacity: introOpacity, y: introY }}
        >
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center justify-between gap-6"
          >
            <p className="eyebrow">Tecnologia para operações reais</p>
            <p className="hidden text-[10px] font-bold uppercase tracking-[.2em] text-white/42 lg:block">Rio de Janeiro · Brasil</p>
          </motion.div>

          <div className="flex flex-1 items-start pt-[7svh] sm:pt-[9svh] lg:items-center lg:pt-0">
            <h1 className="hero-title w-full text-[#f1f3ec]" aria-label="Tecnologia que move negócios">
              <motion.span
                initial={reduce ? false : { opacity: 0, y: 54 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="hero-title-line block"
                style={reduce ? undefined : { x: titleLeftX }}
              >
                Tecnologia
              </motion.span>
              <motion.span
                initial={reduce ? false : { opacity: 0, y: 54 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="hero-title-line hero-title-shift block"
                style={reduce ? undefined : { x: titleRightX }}
              >
                que move <span className="hero-title-accent">negócios.</span>
              </motion.span>
            </h1>
          </div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.34, ease: [0.16, 1, 0.3, 1] }}
            className="grid items-end gap-5 border-t border-white/20 pt-5 lg:grid-cols-[1fr_auto] lg:gap-10 lg:pt-6"
          >
            <p className="max-w-[620px] text-sm leading-relaxed text-white/68 sm:text-base">
              Transformamos processos complexos em sistemas, experiências e automações que trabalham a favor da sua empresa.
            </p>
            <div className="flex items-center gap-3">
              <Link
                to="/contato"
                className="group inline-flex min-h-12 items-center gap-3 rounded-full bg-lime-300 py-1.5 pl-5 pr-1.5 text-xs font-bold text-[#050606] transition-colors hover:bg-[#eff56a] sm:min-h-14 sm:pl-6 sm:text-sm"
                data-testid="hero-cta-primary"
              >
                Iniciar um projeto
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#050606] text-white sm:h-10 sm:w-10">
                  <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
              <a
                href="#transformacao"
                className="hidden h-14 w-14 items-center justify-center rounded-full border border-white/28 text-white transition-colors hover:border-lime-300 hover:text-lime-300 sm:flex"
                aria-label="Rolar para o proximo conteudo"
              >
                <ArrowDown size={18} />
              </a>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
          style={reduce ? { opacity: 0 } : { opacity: revealOpacity, y: revealY }}
          aria-hidden="true"
        >
          <div className="site-shell text-center">
            <p className="mb-5 text-[10px] font-bold uppercase tracking-[.25em] text-lime-300 lg:mb-7">Da ideia à operação</p>
            <p className="display-title mx-auto max-w-5xl text-[clamp(2.9rem,7vw,7.5rem)] leading-[.94] text-white">
              Do problema<br />ao produto.
            </p>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base lg:mt-8">
              Estratégia, design e engenharia trabalhando como uma única equipe.
            </p>
          </div>
        </motion.div>

        <div className="absolute bottom-0 left-0 z-30 h-[3px] w-full bg-white/10" aria-hidden="true">
          <motion.div className="h-full origin-left bg-lime-300" style={{ scaleX: progressScale }} />
        </div>

        <div className="absolute right-6 top-1/2 z-20 hidden -translate-y-1/2 flex-col items-center gap-4 lg:flex" aria-hidden="true">
          <span className="text-[9px] font-bold tracking-[.2em] text-white/38 [writing-mode:vertical-rl]">EXPLORE</span>
          <span className="h-16 w-px bg-white/25" />
          <span className="h-1.5 w-1.5 rounded-full bg-lime-300 shadow-[0_0_16px_rgba(217,224,33,.75)]" />
        </div>

        <div className="absolute bottom-[11.5rem] right-[5%] z-20 hidden gap-2 lg:flex" aria-hidden="true">
          {SERVICE_LABELS.map((label, index) => (
            <span key={label} className="border border-white/18 bg-black/25 px-3 py-2 text-[9px] font-bold uppercase tracking-[.16em] text-white/60 backdrop-blur-sm">
              0{index + 1} {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
